import { useCallback, useRef } from 'react'
import { Button, IconButton } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { FiArrowLeft } from 'react-icons/fi'
import { useAuth } from '../../hooks/useAuth'
import { useIsMobile } from '../../hooks/useIsMobile'
import { readGrantMirror } from '../../lib/grantMirror'
import { readOrganizerCanvass } from '../../lib/organizerCanvass'
import { confirmReturnToOrganizer } from '../../lib/organizerReturnGate'
import { syncEngine } from '../../lib/syncEngine'

/**
 * [Canvass this, CT-9/CT-10] Canvasser-shell "Back to organizer". Renders only for an organizer whose
 * marker names the grant currently in the mirror. It SETS THE GRANT SESSION ASIDE: never logout,
 * wipe or clear the mirror — the queue drains later via SetAsideGrantSync. UX only; server trusts neither.
 */
export function BackToOrganizerButton() {
  const { isOrganizer } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const marker = readOrganizerCanvass()
  const show = isOrganizer && !!marker && marker.grantId === readGrantMirror()?.grantId
  const campaignId = marker?.campaignId
  const busy = useRef(false)

  const onClick = useCallback(async () => {
    if (!campaignId || busy.current) return
    busy.current = true
    try {
      if (!(await confirmReturnToOrganizer())) return
      if (navigator.onLine) {
        try {
          await syncEngine.sync()
        } catch {
          // best-effort: a sync failure never blocks the return
        }
      }
      navigate(`/dashboard/campaigns/${campaignId}`)
    } finally {
      busy.current = false
    }
  }, [campaignId, navigate])

  if (!show) return null

  return isMobile ? (
    <IconButton aria-label="Back to organizer" variant="outline" size="sm" minW="44px" minH="44px" onClick={onClick}>
      <FiArrowLeft />
    </IconButton>
  ) : (
    <Button variant="outline" size="sm" minH="44px" onClick={onClick}>
      <FiArrowLeft /> Back to organizer
    </Button>
  )
}
